import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import { useNavigate, useLocation } from 'react-router';

import keycloak from '../keycloak';
import { onTokenRefreshed } from '../utils/tokenUtils';

export const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/** Session expires after this many seconds without user activity (matches Keycloak ssoSessionIdleTimeout) */
const IDLE_TIMEOUT_S = 900; // 15 minutes

/** Warn the user this many seconds before the session expires */
const EXPIRY_WARNING_THRESHOLD_S = 120; // 2 minutes

/** DOM events that count as user activity and therefore extend the session */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart'];

/**
 * Backoff before each retry of a failed automatic token refresh, in ms.
 * Long enough to ride out a Keycloak rollout (mimir-dev redeploys on every merge to
 * develop, and the deployment has no readiness probe), short enough to still expire
 * the session promptly once the refresh token itself is dead.
 */
const REFRESH_RETRY_DELAYS_MS = [2000, 5000, 10000, 20000, 30000];

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const initialized = useRef(false);
    const warningTimerRef = useRef(null);
    const expiryTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const activitySinceRefreshRef = useRef(false);
    const refreshRetryTimerRef = useRef(null);

    const clearSessionTimers = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
        if (refreshRetryTimerRef.current) clearTimeout(refreshRetryTimerRef.current);
        warningTimerRef.current = null;
        expiryTimerRef.current = null;
        refreshRetryTimerRef.current = null;
    }, []);

    const expireSession = useCallback(() => {
        clearSessionTimers();
        setSessionExpired(true);
        setSessionExpiringSoon(false);
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
    }, [clearSessionTimers]);

    /**
     * Schedule the "expiring soon" warning and the hard expiry.
     * The refresh token carries Keycloak's authoritative session expiry, so it is
     * preferred; otherwise fall back to last activity + the idle timeout.
     */
    const scheduleSessionTimers = useCallback(() => {
        clearSessionTimers();

        const refreshExp = keycloak.refreshTokenParsed?.exp;
        const expiresAtMs = refreshExp
            ? refreshExp * 1000
            : lastActivityRef.current + IDLE_TIMEOUT_S * 1000;
        const expiresInMs = expiresAtMs - Date.now();

        if (expiresInMs <= 0) {
            expireSession();
            return;
        }

        const warnInMs = expiresInMs - EXPIRY_WARNING_THRESHOLD_S * 1000;
        if (warnInMs > 0) {
            warningTimerRef.current = setTimeout(() => setSessionExpiringSoon(true), warnInMs);
        } else {
            setSessionExpiringSoon(true);
        }

        expiryTimerRef.current = setTimeout(expireSession, expiresInMs);
    }, [clearSessionTimers, expireSession]);

    /**
     * Refresh the token, retrying while the failure still looks transient.
     *
     * keycloak-js fires onTokenExpired exactly once per token, so a single failed
     * refresh used to end the session outright - a network blip or a Keycloak restart
     * kicked out every active user with no second chance. A refresh token that has not
     * yet expired means the session is still alive at Keycloak and the call failed for
     * some other reason, so it is worth retrying; once it has expired, the session
     * really is gone and retrying cannot help.
     */
    const refreshWithRetry = useCallback((attempt = 0) => {
        keycloak.updateToken(30)
            .then((refreshed) => {
                activitySinceRefreshRef.current = false;
                if (refreshed) {
                    setToken(keycloak.token);
                    setSessionExpired(false);
                    setSessionExpiringSoon(false);
                }
                scheduleSessionTimers();
            })
            .catch(() => {
                const refreshExp = keycloak.refreshTokenParsed?.exp;
                const sessionMayStillLive = typeof refreshExp === 'number' && refreshExp * 1000 > Date.now();

                if (sessionMayStillLive && attempt < REFRESH_RETRY_DELAYS_MS.length) {
                    refreshRetryTimerRef.current = setTimeout(
                        () => refreshWithRetry(attempt + 1),
                        REFRESH_RETRY_DELAYS_MS[attempt]
                    );
                    return;
                }
                expireSession();
            });
    }, [scheduleSessionTimers, expireSession]);

    const registerActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
        activitySinceRefreshRef.current = true;
    }, []);

    const refreshSession = useCallback(async () => {
        try {
            const refreshed = await keycloak.updateToken(300); // force refresh
            if (refreshed || keycloak.authenticated) {
                lastActivityRef.current = Date.now();
                activitySinceRefreshRef.current = false;
                // Restore isAuthenticated/user/roles too, not just the token - this
                // recovers from a hard `sessionExpired` (not just the warning state).
                // Without this, isAuthenticated stayed false forever after a
                // successful "Logga in igen", leaving OrganisationContext's
                // token-decoding effect permanently gated off and the org/role
                // selector stuck asking the user to pick again.
                setIsAuthenticated(true);
                setUser({
                    id: keycloak.subject,
                    username: keycloak.tokenParsed?.preferred_username,
                    email: keycloak.tokenParsed?.email,
                    firstName: keycloak.tokenParsed?.given_name,
                    lastName: keycloak.tokenParsed?.family_name,
                });
                setRoles(keycloak.tokenParsed?.roles || []);
                setToken(keycloak.token);
                setSessionExpired(false);
                setSessionExpiringSoon(false);
                scheduleSessionTimers();
            }
        } catch {
            expireSession();
            // The session is gone for good — send the user through a fresh login
            // instead of leaving them stuck in the modal.
            keycloak.login();
        }
    }, [scheduleSessionTimers, expireSession]);

    const dismissExpiryWarning = useCallback(() => {
        setSessionExpiringSoon(false);
    }, []);

    useEffect(() => {
        const initKeycloak = async () => {
            if (initialized.current) return;
            initialized.current = true;

            try {
                const authenticated = await keycloak.init({
                    onLoad: 'login-required',
                    pkceMethod: 'S256',
                    checkLoginIframe: false,
                });

                setIsAuthenticated(authenticated);

                if (authenticated) {
                    setUser({
                        id: keycloak.subject,
                        username: keycloak.tokenParsed?.preferred_username,
                        email: keycloak.tokenParsed?.email,
                        firstName: keycloak.tokenParsed?.given_name,
                        lastName: keycloak.tokenParsed?.family_name,
                    });
                    setToken(keycloak.token);
                    lastActivityRef.current = Date.now();
                    activitySinceRefreshRef.current = false;
                    scheduleSessionTimers();

                    if (location.pathname === '/') navigate('/start');
                    const roles = keycloak.tokenParsed?.roles || [];
                    setRoles(roles);
                } else {
                    if (location.pathname !== '/') navigate('/');
                }

                keycloak.onTokenExpired = () => {
                    // Only extend the session when the user has actually been active
                    // since the last refresh. Refreshing unconditionally would keep
                    // the session alive forever and defeat the idle timeout.
                    if (!activitySinceRefreshRef.current) return;

                    refreshWithRetry();
                };

                setLoading(false);
            } catch (error) {
                console.error('Keycloak initialization error:', error);
                setLoading(false);
            }
        };

        initKeycloak();

        return () => {
            clearSessionTimers();
        };
    }, []);

    // Keep `token` in sync with any background refresh (e.g. an API call via
    // getValidToken()), not just the ones AuthContext itself triggers. Without
    // this, an inactive user's own token can silently rotate underneath a stale
    // React `token` value, leaving anything decoded from it (org/role data) out
    // of sync until the user interacts again.
    //
    // The timers must be rearmed here too: they are armed from the refresh token's
    // exp, so a background refresh moves the real session expiry forward while the
    // old expiry timer keeps counting down to the previous one - firing
    // expireSession() on a session Keycloak still considers perfectly alive.
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        return onTokenRefreshed((newToken) => {
            setToken(newToken);
            scheduleSessionTimers();
        });
    }, [isAuthenticated, scheduleSessionTimers]);

    // Track user activity so the session is only extended for active users.
    useEffect(() => {
        if (!isAuthenticated) return undefined;

        ACTIVITY_EVENTS.forEach((event) =>
            window.addEventListener(event, registerActivity, { passive: true })
        );
        return () => {
            ACTIVITY_EVENTS.forEach((event) =>
                window.removeEventListener(event, registerActivity)
            );
        };
    }, [isAuthenticated, registerActivity]);

    const login = () => keycloak.login();

    const logout = async () => {
        // Revoke refresh token at Keycloak before clearing local state.
        // This invalidates the session server-side, preventing token reuse after logout.
        if (keycloak.refreshToken) {
            try {
                const revokeUrl = `${keycloak.authServerUrl}realms/${keycloak.realm}/protocol/openid-connect/revoke`;
                await fetch(revokeUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        token: keycloak.refreshToken,
                        token_type_hint: 'refresh_token',
                        client_id: keycloak.clientId,
                    }),
                });
            } catch (err) {
                // Revocation failure must not block the logout flow
                console.warn('Token revocation failed:', err);
            }
        }

        clearSessionTimers();
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        setSessionExpired(false);
        setSessionExpiringSoon(false);
        sessionStorage.removeItem('activeOrgId');
        sessionStorage.removeItem('activeRoleId');
        keycloak.logout({ redirectUri: window.location.origin });
    };

    const value = {
        isAuthenticated,
        user,
        roles,
        token,
        loading,
        login,
        logout,
        keycloak,
        // Session expiry
        sessionExpired,
        sessionExpiringSoon,
        refreshSession,
        dismissExpiryWarning,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

